import * as React from "react";
import { useQuery } from "@apollo/client";
import { LoadingIcon } from "../icons";
import { BackendError } from "@dvrt/graphql-widgets";
import ItemContainer from "./ItemContainer";
import { OngoingMeetingsQuery, OngoingMeetingsDocument } from "./queries.generated";
import { Empty } from "../tools";
import { defineMessage, FormattedMessage } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link } from "@mui/material";
import moment, { Moment } from "moment";

const parseDate = (value: string): Moment => moment.parseZone(value).local();
const formatDate = (value: Moment): string => value.format("YYYY-MM-DD HH:mm");

type Meeting = OngoingMeetingsQuery["meetings"]["items"][number];

export default () => {
    const { data, loading, error } = useQuery<OngoingMeetingsQuery>(OngoingMeetingsDocument, {
        fetchPolicy: "no-cache",
    });

    if (loading) return <LoadingIcon />;

    if (error) return <BackendError error={error} />;

    const MeetingRow = ({ meeting }: { meeting: Meeting }) => {
        const momentStart = parseDate(meeting.startTime);
        const momentEnd = momentStart.clone().add(meeting.length, "minutes");
        return (
            <tr>
                <td>
                    <Link component={RouterLink} to={`/meeting/view/${meeting.id}`}>
                        {meeting.summary}
                    </Link>
                </td>
                <td>
                    {formatDate(momentStart)} ({momentStart.fromNow()})
                </td>
                <td>
                    {formatDate(momentEnd)} ({momentEnd.fromNow()})
                </td>
            </tr>
        );
    };

    const renderMeetingList = () => {
        return (
            <Box
                component="table"
                sx={{
                    "& td, & th": {
                        padding: "3px 6px",
                    },
                    "& th": {
                        textAlign: "left",
                        fontSize: "1.1em",
                    },
                }}
            >
                <thead>
                    <tr>
                        <th>
                            <FormattedMessage id="meetings.summary" defaultMessage="Summary" />
                        </th>
                        <th>
                            <FormattedMessage id="meetings.started" defaultMessage="Started" />
                        </th>
                        <th>
                            <FormattedMessage id="meetings.endTime" defaultMessage="End" />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data.meetings.items.map((m) => (
                        <MeetingRow meeting={m} key={m.id} />
                    ))}
                </tbody>
            </Box>
        );
    };

    return (
        <ItemContainer
            icon="play-circle"
            label={defineMessage({ id: "meeting.ongoing", defaultMessage: "Ongoing meetings" })}
        >
            {data.meetings.items.length ? (
                renderMeetingList()
            ) : (
                <Empty
                    label={defineMessage({
                        id: "meeting.no_ongoing_meeting",
                        defaultMessage: "There are no ongoing meetings right now",
                    })}
                />
            )}
        </ItemContainer>
    );
};
